import type { ProductSchema } from 'integration-layer/.nuxt/armani/product-schema'
import { cleanDoubleSlashes } from 'ufo'

/**
 * Represents a single breadcrumb item with a label and URL.
 * @typedef {Object} Breadcrumb
 * @property {string} label - The display text for the breadcrumb.
 * @property {string} url - The URL path for the breadcrumb.
 */
type Breadcrumb = {
  label: string
  url: string
}

/**
 * Represents page data from Amplience with optional parent page and brand.
 * @typedef {Object} AmpliencePageData
 * @property {string} categoryTitle - The title of the category.
 * @property {string} urlSlug - The URL slug for the page.
 * @property {AmpliencePageData} [parentPage] - Optional parent page data.
 * @property {string} [brand] - Optional brand name.
 */
type AmpliencePageData = {
  categoryTitle: string
  urlSlug: string
  parentPage?: AmpliencePageData | null
  brand?: string
} | null

/**
 * Utility type to add a current page title to an existing type.
 * @template T
 * @typedef {Object} WithPageTitle
 * @property {T} - Original type
 * @property {string} currentPageTitle - Title of the current page
 */
type WithPageTitle<T> = T & { currentPageTitle: string }

/** Alias for Algolia product categories from the product schema */
type AlgoliaProductCategories = ProductSchema['categoryPageId']

/**
 * Parameters required for breadcrumbs from Algolia data source.
 * @typedef {Object} AlgoliaRequiredParams
 * @property {'algolia'} dataOrigin - Indicates the data source is Algolia.
 * @property {AlgoliaProductCategories} productCategories - Product categories from Algolia.
 * @property {string} currentPageTitle - Title of the current page.
 */
type AlgoliaRequiredParams = WithPageTitle<{
  dataOrigin: 'algolia'
  productCategories: AlgoliaProductCategories
}>

/**
 * Parameters required for breadcrumbs from Amplience data source.
 * @typedef {Object} AmplienceRequiredParams
 * @property {'amplience'} dataOrigin - Indicates the data source is Amplience.
 * @property {AmpliencePageData} pageData - Page data from Amplience.
 * @property {string} currentPageTitle - Title of the current page.
 */
type AmplienceRequiredParams = WithPageTitle<{
  dataOrigin: 'amplience'
  pageData: AmpliencePageData
}>

/**
 * Union type of possible breadcrumb parameter sources.
 * @typedef {(AlgoliaRequiredParams | AmplienceRequiredParams)} UseBreadcrumbsParams
 */
type UseBreadcrumbsParams = AmplienceRequiredParams | AlgoliaRequiredParams

/**
 * Composable for generating breadcrumbs from different data sources.
 *
 * @param {UseBreadcrumbsParams} params - Parameters for generating breadcrumbs.
 * @returns {Object} An object containing computed breadcrumbs.
 * @returns {ComputedRef<Breadcrumb[]>} breadcrumbs - Array of breadcrumb items.
 *
 * @example
 * // Using Algolia data source
 * const { breadcrumbs } = useBreadcrumbs({
 *   dataOrigin: 'algolia',
 *   productCategories: [...],
 *   currentPageTitle: 'Product Details'
 * })
 *
 * // Using Amplience data source
 * const { breadcrumbs } = useBreadcrumbs({
 *   dataOrigin: 'amplience',
 *   pageData: {...},
 *   currentPageTitle: 'Category Page'
 * })
 */
export const useBreadcrumbs = (params: UseBreadcrumbsParams) => {
  const { ts, getLocale } = useI18n()
  const locale = getLocale()

  /**
   * Maps a category to a breadcrumb item with optional path handling.
   *
   * @param {string} [name] - Category name.
   * @param {string} [path] - Category path.
   * @param {number} currentIndex - Current index in the breadcrumb array.
   * @param {number} nCategories - Total number of categories.
   * @returns {Breadcrumb} A breadcrumb item.
   * @private
   */
  const mapBreadcrumb = (
    name: string | undefined,
    path: string | undefined,
    currentIndex: number,
    nCategories: number
  ): Breadcrumb => {
    const isLastItem = currentIndex === nCategories - 1
    return {
      label: name ?? '',
      url: !isLastItem ? (cleanDoubleSlashes(path) ?? '') : '',
    }
  }

  /**
   * Builds breadcrumbs from Algolia product categories.
   *
   * @param {WithPageTitle<AlgoliaRequiredParams>} params - Algolia breadcrumb parameters.
   * @returns {Breadcrumb[]} Array of breadcrumb items.
   * @private
   */
  const buildBreadcrumbsFromAlgoliaProductCategories = ({
    currentPageTitle,
    productCategories,
  }: WithPageTitle<AlgoliaRequiredParams>) => {
    const categories = [
      {
        name: ts('breadcrumbs.home'),
        slug: locale,
      },
      ...productCategories,
      {
        name: currentPageTitle,
      },
    ]
    return categories.reduce<Breadcrumb[]>((acc, category, currentIndex) => {
      if (!category?.name) return acc
      const previousPath = acc[currentIndex - 1]?.url ?? ''
      const breadcrumb = mapBreadcrumb(
        category.name,
        `/${previousPath}/${category.slug}`,
        currentIndex,
        categories.length
      )
      acc.push(breadcrumb)
      return acc
    }, [])
  }

  /**
   * Builds breadcrumbs from Amplience page data.
   *
   * @param {WithPageTitle<AmplienceRequiredParams>} params - Amplience breadcrumb parameters.
   * @returns {Breadcrumb[]} Array of breadcrumb items.
   * @private
   */
  const buildBreadcrumbsFromAmpliencePage = ({
    currentPageTitle,
    pageData,
  }: WithPageTitle<AmplienceRequiredParams>) => {
    /**
     * Builds a slug for Amplience pages.
     *
     * @param {AmpliencePageData[]} pages - Array of page data.
     * @param {number} currentPageIndex - Current page index.
     * @returns {string} Constructed page slug.
     * @private
     */
    const buildAmpliencePageSlug = (
      pages: AmpliencePageData[],
      currentPageIndex: number
    ) =>
      pages
        .slice(0, currentPageIndex + 1)
        .reduce((acc, page) => (acc += `${page?.urlSlug ?? ''}/`), '')

    const pages: AmpliencePageData[] = []

    /**
     * Recursively collects parent pages.
     *
     * @param {AmpliencePageData} [page] - Current page data.
     * @private
     */
    const getPages = (page?: AmpliencePageData) => {
      if (!page || !Object.keys(page).length || !page.urlSlug) return
      pages.unshift(page)
      getPages(page.parentPage)
    }

    getPages(pageData)
    pages.unshift({
      categoryTitle: ts('breadcrumbs.home'),
      urlSlug: `/${locale}`,
      parentPage: pageData,
    })

    pages.push({
      categoryTitle: currentPageTitle,
      urlSlug: '',
      parentPage: null,
    })

    return pages.map<Breadcrumb>((page, currentPageIndex) =>
      mapBreadcrumb(
        page?.categoryTitle,
        buildAmpliencePageSlug(pages, currentPageIndex),
        currentPageIndex,
        pages.length
      )
    )
  }

  /**
   * Computed breadcrumbs based on the data source.
   * @type {ComputedRef<Breadcrumb[]>}
   */
  const breadcrumbs = computed(() => {
    if (params.dataOrigin === 'algolia' && params.productCategories?.length) {
      return buildBreadcrumbsFromAlgoliaProductCategories(params)
    }
    if (params.dataOrigin === 'amplience') {
      return buildBreadcrumbsFromAmpliencePage(params) ?? []
    }
    return []
  })

  useCustomBreadcrumbsSchema(breadcrumbs.value)
  return { breadcrumbs }
}
